import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Role, hasRole } from '@nexa/auth';
import { useSession } from '@/auth/use-session';
import { Button } from '@/components/ui/button';
import { newIdempotencyKey } from '@/lib/uuid';
import { lccApi, lccErrorCode } from '@/lib/lcc/api';
import { currentPriceOf, PRICE_CHANGED, PRICE_UNAVAILABLE, requotedOfferOf } from '@/lib/lcc/price-refusal';
import type { LccCandidateRequest, LccOffer, LccPrice, LccPurchase, LccSearch, LccTraveler } from '@/lib/lcc/types';
import { searchFormSchema, type SearchForm } from '@/lib/lcc/forms';
import { LccTravelers } from './lcc.travelers';
import { ViewTicketButton } from '@/components/flight-ticket/view-ticket-button';

interface Props {
  caseUrn: string;
  groupId: string;
  origin: string;
  destination: string;
  passengerIds: string[];
  initiallyExpanded?: boolean;
  /**
   * The airline has no flight for this PNR (PO 2026-10-28): search other airlines by itself
   * for this date, once, and show the offers as a proposal a supervisor approves.
   */
  autoSearch?: { departureDate: string };
}
const field = 'w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm';
/** Two airline prices are the same amount in the same currency (`null` = not available). */
const samePrice = (a: LccPrice | null, b: LccPrice | null) =>
  a === b || (!!a && !!b && a.amount === b.amount && a.currency === b.currency);

export function LccPanel(props: Props) {
  const { t } = useTranslation('lcc');
  const [open, setOpen] = useState(props.initiallyExpanded ?? false);
  return (
    <section className="rounded-md border border-border p-3" data-testid="lcc-panel">
      <Button variant="outline" size="sm" onClick={() => setOpen(!open)} aria-expanded={open}>
        {t('title')}
      </Button>
      {open ? <LccWorkspace key={`${props.caseUrn}:${props.groupId}`} {...props} /> : null}
    </section>
  );
}

/**
 * The attempts that may hold (or have) an order: at most one per group, and it blocks another
 * purchase. A FAILED attempt bought nothing and rebooking released the group (#769): it is history.
 */
const LIVE_STATUSES: ReadonlySet<LccPurchase['status']> = new Set(['SUBMITTED', 'PENDING', 'UNKNOWN', 'CONFIRMED']);

/** The age codes a purchase answers when a date of birth is not the age the offer was quoted for. */
const AGE_MISMATCH_CODES: ReadonlySet<string> = new Set(['PASSENGER_AGE_MISMATCH', 'ATLAS_PASSENGER_AGE_MISMATCH']);

/** A refusal in words: an age mismatch says «re-quote» explicitly; anything else names its code. */
/**
 * Refresh a quote. The server re-quotes an expired quote, or one another group's purchase already
 * used: at the same price it answers the new quote; at another price it refuses `PRICE_CHANGED`
 * naming the new quote — which is then the one shown, at its price, to approve.
 */
async function refreshQuote(base: LccOffer): Promise<{ offer: LccOffer; requoted: boolean }> {
  try {
    return { offer: await lccApi.refresh(base.id), requoted: false };
  } catch (e) {
    const price = currentPriceOf(e);
    const requoted = requotedOfferOf(e);
    if (!price || !requoted) throw e;
    return {
      offer: { ...base, id: requoted.id, price, ...(requoted.expiresAt ? { expiresAt: requoted.expiresAt } : {}) },
      requoted: true,
    };
  }
}

function lccErrorText(t: TFunction<'lcc'>, code: string): string {
  if (code === 'SOLD_OUT') return t('soldOutRequote');
  return AGE_MISMATCH_CODES.has(code) ? t('ageMismatchRequote') : t('error', { code });
}

function LccWorkspace({ caseUrn, groupId, origin, destination, passengerIds, autoSearch }: Props) {
  const { t } = useTranslation('lcc');
  const { role } = useSession();
  const supervisor = hasRole(role, Role.OPS_SUPERVISOR);
  const queryClient = useQueryClient();
  const purchasesKey = ['lcc', 'purchases', caseUrn, groupId];
  const providerQuery = useQuery({
    queryKey: ['lcc', 'providers'],
    queryFn: lccApi.providers,
    retry: false,
  });
  const purchasesQuery = useQuery({
    queryKey: purchasesKey,
    queryFn: () => lccApi.purchases(caseUrn, groupId),
    retry: false,
    staleTime: 0,
  });
  const providers = providerQuery.data?.providers ?? [];
  const purchases = purchasesQuery.data?.purchases ?? [];
  const loaded = providerQuery.isSuccess && purchasesQuery.isSuccess && !purchasesQuery.isFetching;
  const loadFailed = providerQuery.isError || purchasesQuery.isError;
  const setPurchases = (value: LccPurchase[] | ((prior: LccPurchase[]) => LccPurchase[])) =>
    queryClient.setQueryData<{ purchases: LccPurchase[] }>(purchasesKey, (prior) => ({
      purchases: typeof value === 'function' ? value(prior?.purchases ?? []) : value,
    }));
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const searchMutation = useMutation({
    mutationFn: (input: LccCandidateRequest) => lccApi.candidates(input),
    retry: false,
  });
  const offers = searchMutation.data?.candidates.map((candidate) => candidate.offer) ?? [];
  const searched = searchMutation.isSuccess;
  const partialErrors = searchMutation.data?.errors ?? [];
  const [selected, setSelected] = useState<LccOffer>();
  const [search, setSearch] = useState<LccSearch>();
  const [review, setReview] = useState<LccTraveler[]>();
  const [draft, setDraft] = useState<LccTraveler[]>();
  const [accepted, setAccepted] = useState(false);
  const attemptKey = ['lcc', 'attempted', caseUrn, groupId];
  const [attempted, setAttempted] = useState(
    () => queryClient.getQueryData<boolean>(attemptKey) === true,
  );
  const [uncertain, setUncertain] = useState(
    () => queryClient.getQueryData<boolean>(attemptKey) === true,
  );
  const [changedPrice, setChangedPrice] = useState(false);
  const selectedProvider = providers.find((p) => p.id === selected?.provider);
  const blocked = attempted || purchases.some((p) => LIVE_STATUSES.has(p.status));
  const tooLarge = passengerIds.length === 0 || passengerIds.length > 9;
  const requirementsQuery = useQuery({
    queryKey: ['lcc', 'requirements', caseUrn, groupId, origin, destination, passengerIds],
    queryFn: () =>
      lccApi.candidates({
        caseUrn,
        groupId,
        origin,
        destination,
        cabin: 'ECONOMY',
        currency: 'EUR',
        passengers: passengerIds.map((id) => ({ id })),
      }),
    enabled: loaded && !blocked && !tooLarge,
    retry: false,
  });
  const searchForm = useForm<SearchForm>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      origin,
      destination,
      currency: 'EUR',
      departureDate: '',
      passengers: passengerIds.map((id) => ({ id })),
    },
  });

  async function run(action: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(await lccErrorCode(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function searchFlights(data: SearchForm) {
    const input: LccSearch = {
      ...data,
      caseUrn,
      groupId,
      cabin: 'ECONOMY',
    };
    void run(async () => {
      setSelected(undefined);
      setReview(undefined);
      setDraft(undefined);
      setAccepted(false);
      searchMutation.reset();
      await searchMutation.mutateAsync(input);
      setSearch(input);
    });
  }

  async function reviewPurchase(travelers: LccTraveler[]) {
    if (!selected) return;
    await run(async () => {
      const { offer: refreshed } = await refreshQuote(selected);
      setChangedPrice(!samePrice(refreshed.price, selected.price));
      setSelected(refreshed);
      setReview(travelers);
      setDraft(travelers);
      setAccepted(false);
    });
  }

  async function purchase() {
    // Approve at price (commercial-contract §6.14): only a price the supervisor can see is
    // ever approved — an unavailable price is never bought.
    const approvedPrice = selected?.price;
    if (
      !selected ||
      !approvedPrice ||
      !review ||
      !accepted ||
      !supervisor ||
      blocked ||
      !selectedProvider?.bookingEnabled
    )
      return;
    if (selected.expiresAt && Date.parse(selected.expiresAt) <= Date.now()) {
      setError('OFFER_EXPIRED');
      return;
    }
    await run(async () => {
      setAttempted(true);
      queryClient.setQueryData(attemptKey, true);
      // One logical attempt, no automatic HTTP retry. Never put travelers in
      // query caches, localStorage, analytics or logs. Clear them on submission.
      const travelers = review;
      setReview(undefined);
      setDraft(undefined);
      try {
        const result = await lccApi.purchase(
          {
            offerId: selected.id,
            caseUrn,
            groupId,
            approvedPrice,
            travelers,
            acceptanceConfirmed: true,
          },
          newIdempotencyKey(),
        );
        setPurchases((prior) => [result, ...prior.filter((p) => p.id !== result.id)]);
        if (result.status === 'FAILED') {
          // A definite refusal bought nothing (#769): the travelers come back to correct, and a
          // new review, consent and idempotency key buy again.
          setAttempted(false);
          queryClient.setQueryData(attemptKey, false);
          setDraft(travelers);
          setAccepted(false);
        }
      } catch (e) {
        const code = await lccErrorCode(e);
        if (code === PRICE_CHANGED || code === PRICE_UNAVAILABLE) {
          // Refused before anything was re-quoted or bought: nothing exists at the supplier.
          // A new price is shown on the same review, to be approved again (consent reset).
          setAttempted(false);
          queryClient.setQueryData(attemptKey, false);
          setUncertain(false);
          const current = currentPriceOf(e);
          if (current) {
            // A re-quote (another group's purchase had used the provider's offer) names the
            // NEW quote: that is the one approved and bought.
            const requoted = requotedOfferOf(e);
            setSelected({
              ...selected,
              ...(requoted ? { id: requoted.id, ...(requoted.expiresAt ? { expiresAt: requoted.expiresAt } : {}) } : {}),
              price: current,
            });
            setChangedPrice(true);
          }
          setReview(travelers);
          setDraft(travelers);
          setAccepted(false);
          throw e;
        }
        if (
          [
            'PRICE_CHANGED_REFRESH_REQUIRED',
            'ITINERARY_CHANGED_REFRESH_REQUIRED',
            'OFFER_EXPIRED',
            // a date of birth that is not the quoted age: refused before any journal or order
            'PASSENGER_AGE_MISMATCH',
            // the re-quote found the flight no longer offered to the party: nothing bought
            'SOLD_OUT',
          ].includes(code)
        ) {
          // These domain rejections happen before a purchase journal or vendor
          // order exists. Require a fresh quote review and renewed consent.
          setAttempted(false);
          queryClient.setQueryData(attemptKey, false);
          setUncertain(false);
          setDraft(travelers);
          setAccepted(false);
          throw e;
        }
        setUncertain(true);
        try {
          setPurchases((await lccApi.purchases(caseUrn, groupId)).purchases);
        } catch {
          /* Keep the uncertain state. */
        }
        throw e;
      }
    });
  }

  // The automatic search: once, as soon as nothing blocks it. Adults the airline types ADT go
  // without an age (`assumedAdults`, confirmed at purchase); a child or infant still needs the
  // form — then the result is dropped and the form asks, exactly as a manual search would.
  const autoRan = useRef(false);
  const [autoState, setAutoState] = useState<'searching' | 'done' | 'needsAges'>('searching');
  const configured = providers.some((p) => p.configured);
  useEffect(() => {
    if (!autoSearch || autoRan.current || !loaded || blocked || tooLarge || !configured) return;
    autoRan.current = true;
    const input = {
      caseUrn,
      groupId,
      origin,
      destination,
      departureDate: autoSearch.departureDate,
      cabin: 'ECONOMY',
      currency: 'USD',
      passengers: passengerIds.map((id) => ({ id })),
    };
    searchForm.setValue('departureDate', autoSearch.departureDate);
    searchForm.setValue('currency', 'USD');
    void run(async () => {
      const result = await searchMutation.mutateAsync(input);
      if (result.status === 'REQUIRES_PASSENGER_DETAILS') {
        searchMutation.reset();
        setAutoState('needsAges');
        return;
      }
      setSearch({ ...input, passengers: [] });
      setAutoState('done');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, loaded, blocked, tooLarge, configured]);

  const assumed = searchMutation.data?.assumedAdults?.length ?? 0;
  const expired = !!selected?.expiresAt && Date.parse(selected.expiresAt) <= Date.now();
  return (
    <div className="mt-3 space-y-4 text-sm">
      {autoSearch ? (
        <p role="status" className="rounded border border-sky-300 bg-sky-50 p-2 text-sky-900 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100" data-testid="lcc-auto">
          {t(`auto.${autoState}`, { date: autoSearch.departureDate })}
          {autoState === 'done' && assumed > 0 ? <> {t('auto.assumedAdults', { count: assumed })}</> : null}
        </p>
      ) : null}
      <p className="text-muted-foreground">{t('intro')}</p>
      <p className="text-muted-foreground">{t('candidateRules')}</p>
      <div className="flex flex-wrap gap-2">
        {providers.map((p) => (
          <span key={p.id} className="rounded border border-border px-2 py-1">
            {p.id === 'duffel' ? 'Duffel' : 'Atlas'} ·{' '}
            {t(`mode.${p.mode}`, { defaultValue: p.mode })} ·{' '}
            {t(p.configured ? 'configured' : 'notConfigured')}
          </span>
        ))}
      </div>
      {error ? (
        <p role="alert" className="rounded border border-destructive/30 p-2 text-destructive" data-testid="lcc-error">
          {error === PRICE_CHANGED && selected?.price
            ? t('priceChangedApprove', { amount: selected.price.amount, currency: selected.price.currency })
            : error === PRICE_UNAVAILABLE
              ? t('priceUnavailableBuy')
              : lccErrorText(t, error)}
        </p>
      ) : null}
      {!loaded ? <p>{t(loadFailed ? 'loadFailed' : 'loading')}</p> : null}
      {busy && blocked ? <p role="status">{t('working')}</p> : null}
      {!blocked && !searched && requirementsQuery.data?.status === 'REQUIRES_PASSENGER_DETAILS' ? (
        <p role="status">{t('detailsRequired')}</p>
      ) : null}
      {uncertain && !purchases.some((p) => p.status === 'CONFIRMED') ? (
        <div role="status" className="space-y-2 rounded border border-amber-500 p-3">
          <p>{t('uncertain')}</p>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                setPurchases((await lccApi.purchases(caseUrn, groupId)).purchases);
              })
            }
          >
            {t('checkStatus')}
          </Button>
        </div>
      ) : null}
      {purchases.map((p) => (
        <div
          key={p.id}
          className="space-y-2 rounded border border-border p-3"
          data-testid="lcc-purchase"
        >
          <p className="font-semibold">
            {p.provider === 'duffel' ? 'Duffel' : 'Atlas'} · {t(`mode.${p.mode}`)} ·{' '}
            {t(`status.${p.status}`)}
          </p>
          {p.mode === 'sandbox' ? (
            <p className="font-medium text-amber-700">{t('sandbox')}</p>
          ) : null}
          {p.price ? (
            <p>
              {t('total')}: {p.price.amount} {p.price.currency}
            </p>
          ) : p.price === null ? (
            <p className="text-muted-foreground">
              {t('total')}: {t('priceUnavailable')}
            </p>
          ) : null}
          {p.bookingReference ? (
            <p>
              {t('bookingReference')}: <strong>{p.bookingReference}</strong>
            </p>
          ) : null}
          {p.orderId ? (
            <p className="break-all">
              {t('orderReference')}: {p.orderId}
            </p>
          ) : null}
          {p.ticketNumbers?.length ? (
            <p>
              {t('tickets')}: {p.ticketNumbers.join(', ')}
            </p>
          ) : null}
          {p.status === 'CONFIRMED' ? (
            <p>{t(p.mode === 'sandbox' ? 'confirmedSandbox' : 'confirmed')}</p>
          ) : p.status === 'FAILED' ? (
            <p className="text-muted-foreground">{t('failed')}</p>
          ) : (
            <p className="text-muted-foreground">{t('pending')}</p>
          )}
          {p.status === 'CONFIRMED' && p.orderId ? (
            <ViewTicketButton kind="lcc-purchase" id={p.id} canResend={supervisor} size="sm" />
          ) : null}
          {p.code ? <p>{lccErrorText(t, p.code)}</p> : null}
          {p.vendorCode ? (
            <p data-testid="lcc-purchase-vendor-reason">
              {p.field
                ? t('vendorReasonField', { vendorCode: p.vendorCode, field: p.field })
                : t('vendorReason', { vendorCode: p.vendorCode })}
            </p>
          ) : null}
          {/* A refusal without a vendor order has nothing to reconcile: rebooking answers it as is. */}
          {p.status !== 'CONFIRMED' && !(p.status === 'FAILED' && !p.orderId) && supervisor ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const updated = await lccApi.reconcile(p.id);
                  setPurchases((current) =>
                    current.map((item) => (item.id === p.id ? updated : item)),
                  );
                })
              }
            >
              {t('checkStatus')}
            </Button>
          ) : null}
        </div>
      ))}
      {loaded && !blocked ? (
        <>
          {tooLarge ? (
            <p>{t('groupSize')}</p>
          ) : (
            <form className="space-y-3" onSubmit={searchForm.handleSubmit(searchFlights)}>
              <fieldset disabled={busy} className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  {t('origin')}
                  <input
                    className={field}
                    {...searchForm.register('origin', {
                      setValueAs: (value: string) => value.toUpperCase(),
                    })}
                    required
                    pattern="[A-Za-z]{3}"
                    maxLength={3}
                  />
                </label>
                <label className="space-y-1">
                  {t('destination')}
                  <input
                    className={field}
                    {...searchForm.register('destination', {
                      setValueAs: (value: string) => value.toUpperCase(),
                    })}
                    required
                    pattern="[A-Za-z]{3}"
                    maxLength={3}
                  />
                </label>
                <label className="space-y-1">
                  {t('departureDate')}
                  <input
                    className={field}
                    type="date"
                    {...searchForm.register('departureDate')}
                    required
                  />
                </label>
                <label className="space-y-1">
                  {t('currency')}
                  <select className={field} {...searchForm.register('currency')}>
                    <option>EUR</option>
                    <option>USD</option>
                    <option>GBP</option>
                  </select>
                </label>
                {passengerIds.map((id, i) => (
                  <label className="space-y-1" key={id}>
                    {t('age', { n: i + 1 })}
                    <input
                      className={field}
                      {...searchForm.register(`passengers.${i}.age`, { valueAsNumber: true })}
                      type="number"
                      min={0}
                      max={120}
                      required
                    />
                  </label>
                ))}
              </fieldset>
              {Object.keys(searchForm.formState.errors).length ? (
                <p role="alert">{t('invalidSearch')}</p>
              ) : null}
              <Button
                size="sm"
                disabled={busy || !providers.some((p) => p.configured)}
                type="submit"
              >
                {t(busy ? 'working' : 'search')}
              </Button>
            </form>
          )}
          {partialErrors.map((e, i) => (
            <p className="text-muted-foreground" key={i}>
              {e.provider}: {e.code}
            </p>
          ))}
          {searched && offers.length === 0 ? <p>{t('empty')}</p> : null}
          <div className="space-y-2">
            {offers.map((offer) => (
              <div key={offer.id} className="space-y-2 rounded-md border border-border p-3">
                <p className="font-medium">
                  {offer.provider === 'duffel' ? 'Duffel' : 'Atlas'} ·{' '}
                  {offer.price ? (
                    <>
                      {offer.price.amount} {offer.price.currency} {t('groupTotal')}
                    </>
                  ) : (
                    <span className="text-muted-foreground">{t('priceUnavailable')}</span>
                  )}
                </p>
                {offer.segments.map((s, i) => (
                  <p key={i}>
                    {s.flightNumber} · {s.origin} → {s.destination}
                    <br />
                    <span className="text-muted-foreground">
                      {s.departureLocal} → {s.arrivalLocal} · {t('localTimes')}
                    </span>
                  </p>
                ))}
                {offer.expiresAt ? (
                  <p>
                    {t('expires')}: {offer.expiresAt}
                  </p>
                ) : null}
                {offer.warnings.map((w, i) => (
                  <p className="text-muted-foreground" key={i}>
                    {t(`warnings.${w}`, { defaultValue: w })}
                  </p>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const { offer: fresh, requoted } = await refreshQuote(offer);
                      setSelected(fresh);
                      setReview(undefined);
                      setDraft(undefined);
                      setAccepted(false);
                      setChangedPrice(requoted);
                    })
                  }
                >
                  {t('choose')}
                </Button>
              </div>
            ))}
          </div>
          {selected && search ? (
            <div className="space-y-3 rounded border border-primary/40 p-3">
              <p className="font-semibold">
                {selected.price
                  ? t('selected', {
                      provider: selected.provider,
                      amount: selected.price.amount,
                      currency: selected.price.currency,
                    })
                  : `${selected.provider} · ${t('priceUnavailable')}`}
              </p>
              {!selected.price ? (
                <p role="status" className="text-muted-foreground">
                  {t('priceUnavailableBuy')}
                </p>
              ) : null}
              <p>{t('quoteOnly')}</p>
              {selected.mode === 'sandbox' ? (
                <p className="font-medium text-amber-700">{t('sandbox')}</p>
              ) : (
                <p className="font-medium">{t('live')}</p>
              )}
              {!selectedProvider?.bookingEnabled ? <p>{t('bookingDisabled')}</p> : null}
              {!supervisor ? (
                <p>{t('supervisor')}</p>
              ) : !review ? (
                <LccTravelers
                  key={selected.id}
                  passengers={selected.passengers}
                  departureDate={search.departureDate}
                  disabled={busy || !selectedProvider?.bookingEnabled}
                  onReview={reviewPurchase}
                  initialTravelers={draft}
                />
              ) : (
                <>
                  {changedPrice ? <p role="status">{t('priceChanged')}</p> : null}
                  <p className="font-medium">{t('reviewTitle')}</p>
                  {selected.segments.map((s, i) => (
                    <p key={i}>
                      {s.flightNumber}: {s.origin} → {s.destination} · {s.departureLocal}
                    </p>
                  ))}
                  <ul>
                    {review.map((p) => (
                      <li key={p.id}>
                        {p.givenName} {p.familyName} · {p.bornOn}
                      </li>
                    ))}
                  </ul>
                  <p className="font-semibold">
                    {t('total')}:{' '}
                    {selected.price
                      ? `${selected.price.amount} ${selected.price.currency}`
                      : t('priceUnavailable')}
                  </p>
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={(e) => setAccepted(e.target.checked)}
                    />
                    {t('acceptance')}
                  </label>
                  {expired ? <p>{t('expired')}</p> : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={
                        busy || !accepted || expired || !selected.price || !selectedProvider?.bookingEnabled
                      }
                      onClick={() => void purchase()}
                    >
                      {selected.price
                        ? t('buy', { amount: selected.price.amount, currency: selected.price.currency })
                        : t('priceUnavailable')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        setReview(undefined);
                        setAccepted(false);
                      }}
                    >
                      {t('back')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
